import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Keep-style masonry: cards keep their order left to right, and each one
// drops into whichever column is currently shortest, so a tall card never
// leaves a hole under its neighbours.
//
// Cards are absolutely positioned rather than moved between column
// elements: moving a card to another column would remount it, and a card
// someone is typing into would lose focus and its unsaved text whenever the
// layout shifted.

interface MasonryProps {
  children: React.ReactNode;
  /** Narrowest a column may get before one is dropped. */
  minColumnWidth?: number;
  gap?: number;
  className?: string;
}

const Masonry: React.FC<MasonryProps> = ({ children, minColumnWidth = 340, gap = 16, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemEls = useRef(new Map<string, HTMLDivElement>());
  const [width, setWidth] = useState(0);
  const [heights, setHeights] = useState<Record<string, number>>({});
  // No glide on the very first placement, only on later reflows.
  const [settled, setSettled] = useState(false);

  const items = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement[];
  const keys = items.map((child, i) => String(child.key ?? i));

  const measure = useCallback(() => {
    const next: Record<string, number> = {};
    itemEls.current.forEach((el, key) => {
      next[key] = el.offsetHeight;
    });
    setHeights((prev) => {
      const same =
        Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]);
      return same ? prev : next;
    });
  }, []);

  // Measure before paint, so cards never flash stacked at the top. Runs after
  // every render on purpose; both setters bail out when nothing changed, so
  // it settles after one pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (containerRef.current) setWidth(containerRef.current.clientWidth);
    measure();
  });

  // Later changes: the page resizing, a card growing as items are added,
  // a photo finishing loading.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (containerRef.current) setWidth(containerRef.current.clientWidth);
      measure();
    });
    if (containerRef.current) observer.observe(containerRef.current);
    itemEls.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [measure, keys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!settled && width > 0) {
      const id = requestAnimationFrame(() => setSettled(true));
      return () => cancelAnimationFrame(id);
    }
  }, [settled, width]);

  const columns = Math.max(1, Math.floor((width + gap) / (minColumnWidth + gap)));
  const columnWidth = width > 0 ? (width - gap * (columns - 1)) / columns : 0;
  const columnHeights = new Array(columns).fill(0);
  const positions = keys.map((key) => {
    let col = 0;
    for (let c = 1; c < columns; c++) if (columnHeights[c] < columnHeights[col]) col = c;
    const top = columnHeights[col];
    // A card that renders nothing (filtered out by search) takes no room.
    const h = heights[key] ?? 0;
    if (h > 0) columnHeights[col] += h + gap;
    return { left: col * (columnWidth + gap), top };
  });
  const height = Math.max(0, ...columnHeights) - gap;

  return (
    <div
      ref={containerRef}
      className={`tk-masonry${settled ? ' is-settled' : ''} ${className}`}
      style={{ height: Math.max(height, 0) }}
    >
      {items.map((child, i) => {
        const key = keys[i];
        return (
          <div
            key={key}
            ref={(el) => {
              if (el) itemEls.current.set(key, el);
              else itemEls.current.delete(key);
            }}
            className="tk-masonry-item"
            style={{
              width: columnWidth || '100%',
              transform: `translate(${positions[i].left}px, ${positions[i].top}px)`,
              visibility: width > 0 ? 'visible' : 'hidden',
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

export default Masonry;
